/**
 * Axios error postprocessing
 *
 * @package Muval Frontend SPA
 * @author Tim Maier
 */
import { toast } from 'vue-sonner';
import * as Sentry from '@sentry/vue';
import { useClipboard } from '@vueuse/core';
import { isCanceledRequest, RequestCanceledError } from './requestCancellation';
import { ABORTED_MESSAGE, isNoiseAxiosError } from './axiosNoise';

export { isCanceledRequest, RequestCanceledError };

const { copy } = useClipboard({
    legacy: true,
});

/**
 * The path of a request URL, without the query string, fragment or userinfo.
 *
 * This value goes on the operator's clipboard, so anything a URL can carry that
 * we would not want pasted into a support thread is dropped. Axios usually keeps
 * request params in `config.params` rather than `config.url`, so in practice the
 * query is already absent — but a call that builds its own query string inline
 * would put an operator-entered search term (a customer name, email or phone)
 * straight into the copied text, and nothing stops one being written.
 *
 * `host` deliberately rather than `hostname`, so a non-default port survives;
 * userinfo lives in `username`/`password` and is dropped by not reading them.
 */
function requestPathForDiagnostics(url) {
    if (typeof url !== 'string' || url === '') {
        return null;
    }

    const RELATIVE_BASE = 'http://relative.invalid';

    try {
        const parsed = new URL(url, RELATIVE_BASE);

        return parsed.origin === RELATIVE_BASE ?
                parsed.pathname
            :   `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
    } catch {
        // URL is available in every browser the CRM supports, so this is only
        // reached for input that is not a URL at all. Still better than copying
        // it verbatim.
        return url.split(/[?#]/)[0];
    }
}

/**
 * Build the payload behind "Copy details" on a transport failure.
 *
 * An allowlist, deliberately, rather than trimming fields off the error. The
 * obvious `JSON.stringify({ error })` calls AxiosError.toJSON(), which
 * serialises the whole request config — and plugins/axios.js puts a live
 * `Authorization: Bearer <token>` in config.headers, so that would put a
 * working credential on the operator's clipboard. Axios only redacts when
 * config.redact is set, and nothing here sets it.
 *
 * It also fails better: a non-Axios Error has no enumerable own properties, so
 * `JSON.stringify({ error })` yields `{}`. Naming the fields always produces
 * something support can read.
 */
function copyableTransportFailure(error) {
    return JSON.stringify(
        {
            message: error?.message ?? null,
            code: error?.code ?? null,
            method: error?.config?.method ?? null,
            url: requestPathForDiagnostics(error?.config?.url),
            at: new Date().toISOString(),
        },
        null,
        2,
    );
}

/**
 * Shown for any 5xx. With APP_DEBUG off in production (CU-86d4b3rbg) the API
 * returns a bare "Server Error", which is not worth putting in front of an
 * operator, and the detail is in Sentry either way.
 *
 * 4xx deliberately keeps its own message. The switch below only has cases for
 * 401 and 422, so 403, 404, 409 and 429 all fall through to the same branch
 * carrying messages the operator does need to read.
 */
const SERVER_ERROR_MESSAGE = 'There was an error. Please contact support if the problem persists.';

/**
 * Shown whenever the API gives us nothing readable to show instead.
 */
export const GENERIC_ERROR_MESSAGE = 'An error has occurred.';

/**
 * The message to put in front of the operator for a failed request.
 *
 * `||` rather than `??`, deliberately. A bare `abort(403)` raises an
 * HttpException carrying no message, and the non-debug JSON renderer emits
 * `{"message": ""}`. An empty string is neither null nor undefined, so `??`
 * never substitutes and `toast.error('')` paints a completely blank toast —
 * on a 403, alongside the redirect to /not_authorised, so the operator is
 * bounced with no stated reason and support has nothing to triage from
 * (CU-86d4bamvy).
 *
 * Trimmed first, because whitespace renders exactly as blank as an empty
 * string does. A non-string message falls back too: `[object Object]` in a
 * toast is worse than generic copy.
 *
 * Reads either error shape. `processError` is given the raw axios error, whose
 * body is at `.response.data`; every CRM caller downstream is given the object
 * this module returns, whose body is at `.data`. Handling both means a caller
 * with better copy of its own can reuse this rather than re-derive it and get
 * the guards wrong — the first attempt at one such call site hand-rolled
 * `error?.data?.message?.trim()`, which throws on a non-string message because
 * `?.` guards the message but not `.trim`.
 *
 * Exported so the task-status composables can adopt it rather than keep their
 * own copy of the same rule (CU-86d44jc4f).
 */
export function apiErrorMessage(error, fallback = GENERIC_ERROR_MESSAGE) {
    const message = error?.response?.data?.message ?? error?.data?.message;

    return (typeof message === 'string' ? message.trim() : '') || fallback;
}

/**
 * A request timeout, as opposed to the abort `isNoiseAxiosError` drops. ETIMEDOUT is what axios raises
 * instead of ECONNABORTED when `transitional.clarifyTimeoutError` is on.
 */
function isTimeoutAxiosError(error) {
    return (error?.code === 'ECONNABORTED' && error?.message !== ABORTED_MESSAGE) || error?.code === 'ETIMEDOUT';
}

const ID_PLACEHOLDER = '{id}';
const TENANT_PLACEHOLDER = '{tenant}';
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A path segment that identifies a record or carries a value rather than naming a route.
 *
 * Errs towards replacing: a numeric id, a UUID, anything long enough to be a token or
 * reference that contains a digit, anything percent-encoded (an operator-entered value),
 * and anything with an @ (an email). Losing a route word to the placeholder costs some
 * grouping; keeping a token or a customer's details would put them in Sentry.
 */
function isValueSegment(segment) {
    return (
        /^\d+$/.test(segment) ||
        UUID_SEGMENT.test(segment) ||
        (segment.length >= 8 && /\d/.test(segment)) ||
        segment.includes('%') ||
        segment.includes('@')
    );
}

/**
 * The request's path with record ids replaced by `{id}`, so events group per endpoint
 * (`/orders/{id}/notes`). Query string, fragment, origin and userinfo are all dropped:
 * this goes to Sentry, and a query string can carry a token or a search term.
 *
 * A few callers put the tenant slug in the url as well as the base (`${slug}/sales/ics/url`).
 * That segment becomes `{tenant}`, so those endpoints don't split per tenant either.
 */
function templatedRequestPath(url, tenantSlug) {
    if (typeof url !== 'string' || url === '') {
        return null;
    }

    let pathname;

    try {
        pathname = new URL(url, 'http://relative.invalid').pathname;
    } catch {
        pathname = url.split(/[?#]/)[0];
    }

    return pathname
        .split('/')
        .map((segment) => {
            if (tenantSlug && segment === tenantSlug) {
                return TENANT_PLACEHOLDER;
            }

            return isValueSegment(segment) ? ID_PLACEHOLDER : segment;
        })
        .join('/');
}

function baseURLPath(baseURL) {
    if (typeof baseURL !== 'string' || baseURL === '') {
        return null;
    }

    try {
        return new URL(baseURL, 'http://relative.invalid').pathname;
    } catch {
        return null;
    }
}

/**
 * Which API the request went to. `config.url` is relative to the instance's `baseURL`, so
 * `/me` alone could be the central API (`/api/v1`) or a tenant's (`/{slug}/api/v1`, or
 * `/-/api/v1` before a tenant is known). The slug is left out so events don't split per tenant.
 */
function requestApiBase(baseURL) {
    const pathname = baseURLPath(baseURL);

    if (pathname === null) {
        return null;
    }

    if (/^\/api\//.test(pathname)) {
        return 'central';
    }

    return /^\/[^/]+\/api\//.test(pathname) ? 'tenant' : null;
}

/**
 * The tenant slug in a tenant base (`/{slug}/api/v1`), or null for the central API and for
 * `/-/api/v1`, where no tenant is known yet.
 */
function requestTenantSlug(baseURL) {
    const slug = baseURLPath(baseURL)?.match(/^\/([^/]+)\/api\//)?.[1];

    return slug && slug !== '-' ? slug : null;
}

/**
 * What a timeout's Sentry event needs to be diagnosable (CU-86d477tnt). MOVEPRO-CRM-E3 fires
 * `request.ontimeout` with no timeout configured on any CRM instance, and the events carried
 * only the page URL. This adds the failing endpoint and the XHR's own `timeout` at the moment
 * it failed, which shows whether something outside the app is setting it, and to what.
 *
 * `error.request` is the XMLHttpRequest under the xhr adapter. Under another adapter it has
 * no `timeout`, recorded as null rather than guessed.
 */
function timeoutDiagnostics(error) {
    const xhrTimeout = typeof error?.request?.timeout === 'number' ? error.request.timeout : null;
    const apiBase = requestApiBase(error?.config?.baseURL);
    const path = templatedRequestPath(error?.config?.url, requestTenantSlug(error?.config?.baseURL));

    return {
        tags: {
            api_base: apiBase ?? 'unknown',
            api_method: (error?.config?.method ?? 'unknown').toUpperCase(),
            api_path: path ?? 'unknown',
            xhr_timeout: String(xhrTimeout),
        },
        context: {
            api_base: apiBase,
            method: error?.config?.method ?? null,
            path,
            xhr_timeout: xhrTimeout,
            config_timeout: error?.config?.timeout ?? null,
            xhr_ready_state: error?.request?.readyState ?? null,
        },
    };
}

/**
 * A 402 means the request was fine and the bank said no. A declined card is a
 * routine operator outcome, not an app fault, so it must not become a Sentry
 * event — at the rate cards decline it would bury genuine errors.
 */
function isDeclinedPayment(error) {
    return error?.response?.status === 402;
}

/**
 * Helper function to process Axios errors
 *
 * @param error Axios error object
 * @return object Error Muval SFC can use
 */
export function processError(error) {
    // Intentional aborts (route change, superseded request) are noise, not app errors.
    // Handle BEFORE the response/request branches: axios can reject an already-aborted
    // signal with ERR_CANCELED before it creates error.request. A bare `return` made
    // the interceptor Promise.reject(undefined), and form catch blocks that read
    // `error.message` threw TypeError (MOVEPRO-CRM-MA). Handling cancel only inside
    // the error.request branch would let a pre-dispatch cancel fall through to the
    // setup-error path and return code: null, which callers treat as a real form error.
    if (isCanceledRequest(error)) {
        return new RequestCanceledError(error?.message || 'canceled');
    }

    if (
        import.meta.env.VITE_ENV == 'production' &&
        !isNoiseAxiosError(error) &&
        !isDeclinedPayment(error) &&
        error instanceof Error
    ) {
        // The API returns its own Sentry event id on an unhandled 500. Tagging our
        // event with it is the only thing joining this browser-side event to the API
        // exception behind it: the two live in separate Sentry projects, and with
        // APP_DEBUG off the response body carries no other detail (CU-86d4b4pre).
        const apiEventId = error?.response?.data?.sentry_event_id;
        let captureContext = apiEventId ? { tags: { api_event_id: apiEventId } } : undefined;

        if (isTimeoutAxiosError(error)) {
            const diagnostics = timeoutDiagnostics(error);

            captureContext = {
                tags: { ...captureContext?.tags, ...diagnostics.tags },
                contexts: { request_timeout: diagnostics.context },
            };
        }

        Sentry.captureException(error, captureContext);
    }

    let result = {
        code: null,
        message: 'Unknown error',
        errors: null,
        links: null,
        data: null,
    };

    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx

        switch (error.response.status) {
            case 401:
                result.message = apiErrorMessage(error);
                break;

            case 422:
                // toast.error(apiErrorMessage(error)); // Disabled the toast notif for 422 errors.
                result.message = apiErrorMessage(error);
                result.errors = error.response.data.errors;
                result.links = error.response.data.links;
                break;

            default:
                if (error.stack) {
                    delete error.stack;
                }

                if (error?.config?.xSilent === true) {
                    console.log('Skipped toast as request was set to silent:', apiErrorMessage(error));
                } else if (!useUserStore().isAuthenticated) {
                    console.log('Skipped toast as user is not authenticated:', apiErrorMessage(error));
                } else if (error.response.status >= 500) {
                    toast.error(SERVER_ERROR_MESSAGE);
                } else {
                    toast.error(apiErrorMessage(error));
                }

                // Generic on 5xx here too, not just in the toast above. The axios
                // interceptor rejects with this object, and 261 places in the CRM
                // read a caught error's `.message` — several passing it straight
                // to a toast (useQuoteHelper, useTaskStatusMutation,
                // useCrewTaskStatusMutation, useSalesActionNotes). Leaving the raw
                // value here would route the server's own text around the generic
                // toast, which is the whole point of the change. It matters most
                // for the controllers that return `$e->getMessage()` in their own
                // body and so bypass APP_DEBUG entirely (CU-86d2jwmhz).
                result.message = error.response.status >= 500 ? SERVER_ERROR_MESSAGE : apiErrorMessage(error);
                result.errors = error?.response?.data?.errors;

                break;
        }

        result.code = error.response.status;
        result.data = error.response.data;
    } else if (error.request) {
        // The request was made but no response was received. Cancellations are
        // handled at the top of this function, before this branch.

        if (error.stack) {
            delete error.stack;
        }

        result.message = error?.message ?? 'Network Error';
        result.code = error?.code ?? 'ERR_NETWORK';

        if (error?.config?.xSilent === true) {
            console.log('Skipped toast as request was set to silent:', result.message);
        } else if (!useUserStore().isAuthenticated) {
            console.log('Skipped toast as user is not authenticated:', result.message);
        } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            console.log('Skipped toast as browser is offline:', result.message);
        } else {
            // Kept here, unlike the response branch: a transport failure never
            // reaches the API, so there is no server-side event to correlate
            // with, and ERR_NETWORK is deliberately excluded from Sentry as
            // noise. The copied text is the only diagnostic there is.
            toast.error('An error has occurred.', {
                action: {
                    label: 'Copy details',
                    onClick: () => copy(copyableTransportFailure(error)),
                },
            });
        }
    } else {
        // Something happened in setting up the request that triggered an Error
        result.message = 'Axios error: ' + error.message;
    }

    return result;
}
